<template>
  <v-card class="mx-auto" width="95%" style="margin-bottom: 15px;">
    <v-container fluid class="my-5">
      <v-card-title>
        <v-icon left>mdi-history</v-icon>
        {{ $t('components.AuditTrailViewer.title') }}
        <v-spacer></v-spacer>
        <v-btn color="primary" @click="exportAuditLogsData" :loading="exporting">
          <v-icon left>mdi-download</v-icon>
          {{ $t('components.AuditTrailViewer.exportCsv') }}
        </v-btn>
        <v-btn color="secondary" @click="refreshLogs" :loading="loading" class="ml-2">
          <v-icon left>mdi-refresh</v-icon>
          {{ $t('components.AuditTrailViewer.refresh') }}
        </v-btn>
      </v-card-title>

      <!-- Filters -->
      <v-card-text>
        <v-row>
          <v-col cols="12" md="3">
            <v-menu
              v-model="startDateMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="filters.startDate"
                  :label="$t('components.AuditTrailViewer.startDateLabel')"
                  prepend-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  clearable
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="filters.startDate"
                @input="startDateMenu = false; loadAuditLogsData()"
              ></v-date-picker>
            </v-menu>
          </v-col>

          <v-col cols="12" md="3">
            <v-menu
              v-model="endDateMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="filters.endDate"
                  :label="$t('components.AuditTrailViewer.endDateLabel')"
                  prepend-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  clearable
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="filters.endDate"
                @input="endDateMenu = false; loadAuditLogsData()"
              ></v-date-picker>
            </v-menu>
          </v-col>

          <v-col cols="12" md="3">
            <v-select
              v-model="filters.action"
              :items="actionTypes"
              :label="$t('components.AuditTrailViewer.actionTypeLabel')"
              prepend-icon="mdi-filter"
              clearable
              @change="loadAuditLogsData"
            ></v-select>
          </v-col>

          <v-col cols="12" md="3">
            <v-text-field
              v-model="filters.targetUser"
              :label="$t('components.AuditTrailViewer.targetUserEmailLabel')"
              prepend-icon="mdi-account"
              clearable
              @input="debounceLoadLogs"
            ></v-text-field>
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Data Table -->
      <v-data-table
        :headers="headers"
        :items="auditLogs"
        :loading="loading"
        class="elevation-1"
        :server-items-length="totalLogs"
        :options.sync="options"
        @update:options="loadAuditLogsData"
      >
        <template v-slot:item.timestamp="{ item }">
          <span>{{ formatTimestamp(item.timestamp) }}</span>
        </template>

        <template v-slot:item.action="{ item }">
          <v-chip
            :color="getActionColor(item.action)"
            text-color="white"
            small
          >
            <v-icon left small>{{ getActionIcon(item.action) }}</v-icon>
            {{ getActionText(item.action) }}
          </v-chip>
        </template>

        <template v-slot:item.changes="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                small
                v-bind="attrs"
                v-on="on"
                @click="showChanges(item)"
              >
                <v-icon>mdi-eye</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('components.AuditTrailViewer.viewChanges') }}</span>
          </v-tooltip>
        </template>
      </v-data-table>
    </v-container>

    <!-- Changes Dialog -->
    <v-dialog v-model="changesDialog" max-width="600px">
      <v-card>
        <v-card-title>
          <span class="headline">{{ $t('components.AuditTrailViewer.changeDetailsTitle') }}</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="changesDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <v-row v-if="selectedItem">
            <v-col cols="12">
              <strong>{{ $t('components.AuditTrailViewer.actionLabel') }}</strong> {{ getActionText(selectedItem.action) }}
            </v-col>
            <v-col cols="12">
              <strong>{{ $t('components.AuditTrailViewer.performedByLabel') }}</strong> {{ selectedItem.performedByEmail }}
            </v-col>
            <v-col cols="12">
              <strong>{{ $t('components.AuditTrailViewer.targetUserLabel') }}</strong> {{ selectedItem.targetUserEmail }}
            </v-col>
            <v-col cols="12" v-if="selectedItem.modelId">
              <strong>{{ $t('components.AuditTrailViewer.modelIdLabel') }}</strong> {{ selectedItem.modelId }}
            </v-col>
            <v-col cols="12">
              <strong>{{ $t('components.AuditTrailViewer.timestampLabel') }}</strong> {{ formatTimestamp(selectedItem.timestamp) }}
            </v-col>

            <v-col cols="12" v-if="selectedItem.changes">
              <v-card outlined>
                <v-card-title class="subtitle-1">{{ $t('components.AuditTrailViewer.changesTitle') }}</v-card-title>
                <v-card-text>
                  <div class="mb-2">
                    <strong>{{ $t('components.AuditTrailViewer.beforeLabel') }}</strong>
                    <pre class="caption">{{ JSON.stringify(selectedItem.changes.before, null, 2) }}</pre>
                  </div>
                  <div>
                    <strong>{{ $t('components.AuditTrailViewer.afterLabel') }}</strong>
                    <pre class="caption">{{ JSON.stringify(selectedItem.changes.after, null, 2) }}</pre>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'AuditTrailViewer',
  data () {
    return {
      exporting: false,
      startDateMenu: false,
      endDateMenu: false,
      changesDialog: false,
      selectedItem: null,
      debounceTimer: null,

      filters: {
        startDate: null,
        endDate: null,
        action: null,
        targetUser: null
      },

      options: {
        page: 1,
        itemsPerPage: 25,
        sortBy: ['timestamp'],
        sortDesc: [true]
      }
    }
  },

  computed: {
    ...mapGetters('manager', [
      'auditLogs',
      'auditLoading',
      'auditTotal'
    ]),
    loading () {
      return this.auditLoading
    },
    totalLogs () {
      return this.auditTotal
    },
    headers () {
      return [
        { text: this.$t('components.AuditTrailViewer.headerTimestamp'), value: 'timestamp', width: '180px' },
        { text: this.$t('components.AuditTrailViewer.headerAction'), value: 'action', width: '150px' },
        { text: this.$t('components.AuditTrailViewer.headerPerformedBy'), value: 'performedByEmail', width: '200px' },
        { text: this.$t('components.AuditTrailViewer.headerTargetUser'), value: 'targetUserEmail', width: '200px' },
        { text: this.$t('components.AuditTrailViewer.headerModelId'), value: 'modelId', width: '150px' },
        { text: this.$t('components.AuditTrailViewer.headerChanges'), value: 'changes', sortable: false, width: '100px' }
      ]
    },
    actionTypes () {
      return [
        { text: this.$t('components.AuditTrailViewer.actionGrantAccess'), value: 'GRANT_ACCESS' },
        { text: this.$t('components.AuditTrailViewer.actionRevokeAccess'), value: 'REVOKE_ACCESS' },
        { text: this.$t('components.AuditTrailViewer.actionGrantReferenceAccess'), value: 'GRANT_REFERENCE_ACCESS' },
        { text: this.$t('components.AuditTrailViewer.actionRevokeReferenceAccess'), value: 'REVOKE_REFERENCE_ACCESS' },
        { text: this.$t('components.AuditTrailViewer.actionGrantEkrmAccess'), value: 'GRANT_EKRM_ACCESS' },
        { text: this.$t('components.AuditTrailViewer.actionRevokeEkrmAccess'), value: 'REVOKE_EKRM_ACCESS' },
        { text: this.$t('components.AuditTrailViewer.actionDeleteUser'), value: 'DELETE_USER' }
      ]
    }
  },

  methods: {
    ...mapActions('manager', [
      'loadAuditLogs',
      'exportAuditLogs'
    ]),
    async loadAuditLogsData () {
      try {
        const queryParams = {
          limit: this.options.itemsPerPage,
          offset: (this.options.page - 1) * this.options.itemsPerPage
        }

        // Apply filters
        if (this.filters.startDate) {
          queryParams.startDate = new Date(this.filters.startDate).toISOString()
        }
        if (this.filters.endDate) {
          queryParams.endDate = new Date(this.filters.endDate).toISOString()
        }
        if (this.filters.action) {
          queryParams.action = this.filters.action
        }
        if (this.filters.targetUser && this.filters.targetUser.trim()) {
          queryParams.targetUser = this.filters.targetUser.trim()
        }

        await this.loadAuditLogs(queryParams)
      } catch (error) {
        console.error('Error loading audit logs:', error)

        let message = this.$t('components.AuditTrailViewer.loadError') + (error.message || 'Unknown error')

        // Special handling for index errors
        if (error.isIndexError) {
          message = error.message + this.$t('components.AuditTrailViewer.indexErrorHint')
        }

        this.$emit('show-alert', {
          type: 'error',
          message: message
        })
      }
    },

    async exportAuditLogsData () {
      this.exporting = true
      try {
        const queryParams = {}

        // Apply same filters as the current view
        if (this.filters.startDate) {
          queryParams.startDate = new Date(this.filters.startDate).toISOString()
        }
        if (this.filters.endDate) {
          queryParams.endDate = new Date(this.filters.endDate).toISOString()
        }
        if (this.filters.action) {
          queryParams.action = this.filters.action
        }
        if (this.filters.targetUser && this.filters.targetUser.trim()) {
          queryParams.targetUser = this.filters.targetUser.trim()
        }

        const result = await this.exportAuditLogs(queryParams)

        // Create and download the CSV file
        const blob = new Blob([result.csv], { type: 'text/csv' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = result.filename
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)

        this.$emit('show-alert', {
          type: 'success',
          message: this.$t('components.AuditTrailViewer.exportSuccess')
        })
      } catch (error) {
        console.error('Error exporting audit logs:', error)

        let message = this.$t('components.AuditTrailViewer.exportError') + (error.message || 'Unknown error')

        // Special handling for index errors
        if (error.isIndexError) {
          message = error.message + this.$t('components.AuditTrailViewer.indexErrorHint')
        }

        this.$emit('show-alert', {
          type: 'error',
          message: message
        })
      } finally {
        this.exporting = false
      }
    },

    refreshLogs () {
      this.loadAuditLogsData()
    },

    showChanges (item) {
      this.selectedItem = item
      this.changesDialog = true
    },

    debounceLoadLogs () {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(() => {
        this.loadAuditLogsData()
      }, 500)
    },

    formatTimestamp (timestamp) {
      if (!timestamp) return this.$t('components.AuditTrailViewer.timestampNA')

      // Handle various timestamp formats
      let date
      if (timestamp.toDate && typeof timestamp.toDate === 'function') {
        // Firestore Timestamp
        date = timestamp.toDate()
      } else if (timestamp.seconds) {
        // Firestore Timestamp-like object with seconds
        date = new Date(timestamp.seconds * 1000)
      } else if (timestamp._seconds) {
        // Firestore Timestamp serialized format
        date = new Date(timestamp._seconds * 1000)
      } else if (typeof timestamp === 'string' || typeof timestamp === 'number') {
        // ISO string or Unix timestamp
        date = new Date(timestamp)
      } else {
        // Already a Date object or unknown format
        date = timestamp instanceof Date ? timestamp : new Date(timestamp)
      }

      // Validate the date
      if (isNaN(date.getTime())) {
        console.warn('Invalid timestamp received:', timestamp)
        return this.$t('components.AuditTrailViewer.timestampInvalid')
      }

      return date.toLocaleString()
    },

    getActionColor (action) {
      switch (action) {
        case 'GRANT_ACCESS': return 'green'
        case 'REVOKE_ACCESS': return 'orange'
        case 'GRANT_REFERENCE_ACCESS': return 'green'
        case 'REVOKE_REFERENCE_ACCESS': return 'orange'
        case 'GRANT_EKRM_ACCESS': return 'green'
        case 'REVOKE_EKRM_ACCESS': return 'orange'
        case 'DELETE_USER': return 'red'
        default:
          // Handle unknown actions intelligently based on action name
          if (action && action.includes('GRANT')) return 'green'
          if (action && action.includes('REVOKE')) return 'orange'
          return 'grey'
      }
    },

    getActionIcon (action) {
      switch (action) {
        case 'GRANT_ACCESS': return 'mdi-dots-hexagon'
        case 'REVOKE_ACCESS': return 'mdi-dots-hexagon'
        case 'GRANT_REFERENCE_ACCESS': return 'mdi-stamper'
        case 'REVOKE_REFERENCE_ACCESS': return 'mdi-stamper'
        case 'GRANT_EKRM_ACCESS': return 'mdi-head-snowflake'
        case 'REVOKE_EKRM_ACCESS': return 'mdi-head-snowflake'
        case 'DELETE_USER': return 'mdi-account-remove'
        // Group-related actions
        case 'GRANT_GROUP_ACCESS': return 'mdi-account-supervisor'
        case 'REVOKE_GROUP_ACCESS': return 'mdi-account-supervisor'
        case 'CREATE_GROUP': return 'mdi-plus-thick'
        case 'DELETE_GROUP': return 'mdi-delete'
        default:
          // Handle unknown actions intelligently based on action name
          if (action && action.includes('GROUP')) {
            if (action.includes('CREATE')) return 'mdi-plus-thick'
            if (action.includes('DELETE')) return 'mdi-delete'
            return 'mdi-account-supervisor'
          }
          if (action && action.includes('REFERENCE')) return 'mdi-stamper'
          if (action && action.includes('EKRM')) return 'mdi-head-snowflake'
          if (action && action.includes('ACCESS')) return 'mdi-dots-hexagon'
          if (action && action.includes('GRANT')) return 'mdi-plus'
          if (action && action.includes('REVOKE')) return 'mdi-minus'
          return 'mdi-help'
      }
    },

    getActionText (action) {
      switch (action) {
        case 'GRANT_ACCESS': return this.$t('components.AuditTrailViewer.actionGrantAccess')
        case 'REVOKE_ACCESS': return this.$t('components.AuditTrailViewer.actionRevokeAccess')
        case 'GRANT_REFERENCE_ACCESS': return this.$t('components.AuditTrailViewer.actionGrantReferenceAccess')
        case 'REVOKE_REFERENCE_ACCESS': return this.$t('components.AuditTrailViewer.actionRevokeReferenceAccess')
        case 'GRANT_EKRM_ACCESS': return this.$t('components.AuditTrailViewer.actionGrantEkrmAccess')
        case 'REVOKE_EKRM_ACCESS': return this.$t('components.AuditTrailViewer.actionRevokeEkrmAccess')
        case 'DELETE_USER': return this.$t('components.AuditTrailViewer.actionDeleteUser')
        // Group-related actions
        case 'GRANT_GROUP_ACCESS': return this.$t('components.AuditTrailViewer.actionGrantGroupAccess')
        case 'REVOKE_GROUP_ACCESS': return this.$t('components.AuditTrailViewer.actionRevokeGroupAccess')
        case 'CREATE_GROUP': return this.$t('components.AuditTrailViewer.actionCreateGroup')
        case 'DELETE_GROUP': return this.$t('components.AuditTrailViewer.actionDeleteGroup')
        case 'GRANT_GROUP_MODEL_ACCESS': return this.$t('components.AuditTrailViewer.actionGrantGroupModelAccess')
        case 'REVOKE_GROUP_MODEL_ACCESS': return this.$t('components.AuditTrailViewer.actionRevokeGroupModelAccess')
        case 'GRANT_GROUP_REFERENCE_ACCESS': return this.$t('components.AuditTrailViewer.actionGrantGroupReferenceAccess')
        case 'REVOKE_GROUP_REFERENCE_ACCESS': return this.$t('components.AuditTrailViewer.actionRevokeGroupReferenceAccess')
        case 'GRANT_GROUP_EKRM_ACCESS': return this.$t('components.AuditTrailViewer.actionGrantGroupEkrmAccess')
        case 'REVOKE_GROUP_EKRM_ACCESS': return this.$t('components.AuditTrailViewer.actionRevokeGroupEkrmAccess')
        default:
          // Convert raw action names to readable format
          if (!action) return this.$t('components.AuditTrailViewer.actionUnknown')
          return action.split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ')
      }
    }
  },

  mounted () {
    this.loadAuditLogsData()
  },

  watch: {
    options: {
      handler () {
        this.loadAuditLogsData()
      },
      deep: true
    },

    'filters.startDate' () {
      if (this.filters.startDate) {
        this.options.page = 1
        this.loadAuditLogsData()
      }
    },

    'filters.endDate' () {
      if (this.filters.endDate) {
        this.options.page = 1
        this.loadAuditLogsData()
      }
    }
  }
}
</script>

<style scoped>
pre {
  background-color: #f5f5f5;
  padding: 8px;
  border-radius: 4px;
  font-size: 12px;
  overflow-x: auto;
  max-height: 200px;
  overflow-y: auto;
}
</style>
